import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout, Typography, Spin } from '../../../components';
import { FolderOpenOutlined, AppstoreOutlined } from '@ant-design/icons';
import { GetStartedCard } from './Components/Cards/GetStartedCard';
import { CreateMoveGroupModal } from './Components/Modals/CreateMoveGroupModal';
import { CreateApplicationModal } from './Components/Modals/CreateApplicationModal';
import { DragAssignmentConfirmModal } from './Components/Modals/DragAssignmentConfirmModal';
import './MoveGroupsAndApps.scss';
import { MoveGroupsAndAppsSidebar } from './Components/MoveGroupsAndAppsSidebar';
import { DeleteModal } from '../../../components';
import {
    AssignFromUnsortedModal,
    EditAssignedModal,
    EditMoveGroupModal,
    EditApplicationModal,
} from './Components/Modals/ManageModal';
import { MoveGroupsAndAppsTable } from './Components/MoveGroupsAndAppsTable';
import { useMoveGroupsAndAppsData } from './useMoveGroupsAndAppsData';
import { DndContext } from '@dnd-kit/core';
import { useMoveGroupsAndAppsDnD } from './useMoveGroupsAndAppsDnD';
import { useNotificationsPanel } from '../../../providers/NotificationsPanelProvider';

const { Title, Paragraph } = Typography;

export const MoveGroupsAndApps = () => {
    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteModalMessage, setDeleteModalMessage] = useState('');
    const [deleteModalHandler, setDeleteModalHandler] = useState(() => () => {});

    // Helper to show delete modal before deleting
    const showDeleteModal = (message, handler) => {
        setDeleteModalMessage(message);
        setDeleteModalHandler(() => handler);
        setDeleteModalOpen(true);
    };

    const { refreshNotifications } = useNotificationsPanel();

    const {
        // State
        moveGroupModalOpen,
        setMoveGroupModalOpen,
        applicationModalOpen,
        setApplicationModalOpen,
        manageModalOpen,
        setManageModalOpen,
        editMoveGroupModalOpen,
        setEditMoveGroupModalOpen,
        editApplicationModalOpen,
        setEditApplicationModalOpen,
        editingMoveGroup,
        setEditingMoveGroup,
        editingApplication,
        setEditingApplication,
        selectedGroup,
        setSelectedGroup,
        selectedApp,
        setSelectedApp,
        selectedInventory,
        setSelectedInventory,
        selectedUnassigned,
        setSelectedUnassigned,
        selectedRowKeys,

        // Data
        moveGroups,
        applications,
        moveGroupOptions,
        applicationOptions,
        userOptions,
        hasGroupsOrApps,
        isLoading,
        unsortedMachinesCount,
        unsortedInventoryLoading,
        unassignedMachinesCount,
        unassignedInventoryLoading,
        moveGroupData,
        applicationData,
        moveGroupLoading,
        moveGroupFetching,
        applicationLoading,
        applicationFetching,
        pagination,

        // Handlers
        handleCreateMoveGroup,
        handleUpdateMoveGroup,
        handleCreateApplication,
        handleUpdateApplication,
        handleManageApply,
        handleSelectionChange,
        handleEditMoveGroup,
        handleEditApplication,
        handleDeleteMoveGroup,
        handleDeleteApplication,
        handleUnassignApplication,
        handleChangeStatus,
        handlePaginationChange,
        handleDragAssignVMs,
        handleDragReassignApplication,
    } = useMoveGroupsAndAppsData({ refreshNotifications });

    const location = useLocation();
    const locationStateRef = useRef(location.state);

    // Pre-select unassigned inventory view when navigating from a notification link.
    useEffect(() => {
        const view = locationStateRef.current?.view;
        if (view === 'unassigned' || view === 'unsorted') {
            setSelectedUnassigned(true);
        }
    }, []);

    const [pendingDragAssignment, setPendingDragAssignment] = useState(null);

    const handleDragToCreateGroup = (vmData) => {
        setPendingDragAssignment(vmData);
        setMoveGroupModalOpen(true);
    };

    const handleDragToCreateApp = (vmData) => {
        setPendingDragAssignment(vmData);
        setApplicationModalOpen(true);
    };

    const {
        dragAssignModalOpen,
        setDragAssignModalOpen,
        dragAssignModalTitle,
        dragAssignModalMessage,
        dragAssignHandler,
        isDraggingVM,
        draggingVMRecord,
        vmDragTargetRef,
        masterSensors,
        handleMasterDragStart,
        handleMasterDragEnd,
        handleDragAssignment,
    } = useMoveGroupsAndAppsDnD({
        moveGroups,
        applications,
        handleDragAssignVMs,
        handleDragReassignApplication,
        onDragToCreateGroup: handleDragToCreateGroup,
        onDragToCreateApp: handleDragToCreateApp,
    });

    // Handlers for rendering a confirmation modal before deleting
    const handleDeleteMoveGroupWithModal = (groupId) => {
        showDeleteModal('Are you sure you want to delete this move group?', () => handleDeleteMoveGroup(groupId));
    };

    const handleDeleteApplicationWithModal = (appId) => {
        showDeleteModal('Are you sure you want to delete this application?', () => handleDeleteApplication(appId));
    };

    if (isLoading) {
        return (
            <Layout className="move-groups-apps-page">
                <div
                    className="move-groups-apps-main-container"
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
                >
                    <Spin size="large" />
                </div>
            </Layout>
        );
    }

    if (!hasGroupsOrApps) {
        return (
            <Layout className="move-groups-apps-page">
                <div className="move-groups-apps-main-container">
                    <Title level={2} className="move-groups-apps-title">
                        Move Groups and Apps
                    </Title>
                    <Paragraph className="move-groups-apps-desc">
                        Manage your Move Groups and Applications here. Any automatically discovered Applications have
                        been listed below...
                    </Paragraph>
                    <div className="move-groups-apps-cards">
                        <GetStartedCard
                            icon={<FolderOpenOutlined />}
                            title="Move Groups"
                            text="Organize VMs and Applications into move groups to plan and execute migrations more efficiently. Group workloads by dependency, priority, or destination to reduce risk and streamline migration."
                            className="move-groups-apps-card--move-groups"
                            onButtonClick={() => setMoveGroupModalOpen(true)}
                        />
                        <GetStartedCard
                            icon={<AppstoreOutlined />}
                            title="Applications"
                            text="Organize VMs into application-based groups to simplify dependency management during migration and easily assign applications to move groups."
                            className="move-groups-apps-card--applications"
                            onButtonClick={() => setApplicationModalOpen(true)}
                        />
                    </div>
                </div>
                <CreateMoveGroupModal
                    open={moveGroupModalOpen}
                    onCancel={() => setMoveGroupModalOpen(false)}
                    onApply={handleCreateMoveGroup}
                    applications={applicationOptions}
                    users={userOptions}
                />
                <CreateApplicationModal
                    open={applicationModalOpen}
                    onCancel={() => setApplicationModalOpen(false)}
                    onApply={handleCreateApplication}
                    moveGroups={moveGroupOptions}
                />
            </Layout>
        );
    }

    return (
        <Layout className="move-groups-apps-page">
            <DndContext sensors={masterSensors} onDragStart={handleMasterDragStart} onDragEnd={handleMasterDragEnd}>
                <div className="move-groups-apps-main-container move-groups-apps-flex-layout">
                    <MoveGroupsAndAppsSidebar
                        moveGroups={moveGroups}
                        applications={applications}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={setSelectedGroup}
                        selectedApp={selectedApp}
                        setSelectedApp={setSelectedApp}
                        selectedInventory={selectedInventory}
                        setSelectedInventory={setSelectedInventory}
                        onCreateMoveGroup={() => setMoveGroupModalOpen(true)}
                        onCreateApplication={() => setApplicationModalOpen(true)}
                        onEditMoveGroup={handleEditMoveGroup}
                        onEditApplication={handleEditApplication}
                        onDeleteMoveGroup={handleDeleteMoveGroupWithModal}
                        onDeleteApplication={handleDeleteApplicationWithModal}
                        onUnassignApplication={handleUnassignApplication}
                        onChangeStatus={handleChangeStatus}
                        unsortedMachinesCount={unsortedMachinesCount}
                        unsortedMachinesLoading={unsortedInventoryLoading}
                        unassignedMachinesCount={unassignedMachinesCount}
                        unassignedMachinesLoading={unassignedInventoryLoading}
                        onDragAssignment={handleDragAssignment}
                        isDraggingVM={isDraggingVM}
                        draggingVMRecord={draggingVMRecord}
                        onVMDragOver={(target) => {
                            vmDragTargetRef.current = target;
                        }}
                        selectedUnassigned={selectedUnassigned}
                        setSelectedUnassigned={setSelectedUnassigned}
                    />
                    <MoveGroupsAndAppsTable
                        moveGroupData={moveGroupData}
                        applicationData={applicationData}
                        moveGroupLoading={moveGroupLoading}
                        moveGroupFetching={moveGroupFetching}
                        applicationLoading={applicationLoading}
                        applicationFetching={applicationFetching}
                        pagination={pagination}
                        handlePaginationChange={handlePaginationChange}
                        selectedGroup={selectedGroup}
                        selectedApp={selectedApp}
                        selectedInventory={selectedInventory}
                        selectedUnassigned={selectedUnassigned}
                        moveGroups={moveGroups}
                        apps={applications}
                        onOpenManageModal={() => setManageModalOpen(true)}
                        onSelectionChange={handleSelectionChange}
                        onChangeStatus={handleChangeStatus}
                        onDragAssignment={handleDragAssignment}
                        selectedRowKeys={selectedRowKeys}
                    />
                </div>
            </DndContext>
            <CreateMoveGroupModal
                open={moveGroupModalOpen}
                onCancel={() => {
                    setMoveGroupModalOpen(false);
                    setPendingDragAssignment(null);
                }}
                onApply={(values) =>
                    handleCreateMoveGroup(
                        values,
                        pendingDragAssignment
                            ? (newId) => {
                                  handleDragAssignVMs(pendingDragAssignment.sourceIds, newId, undefined);
                                  setPendingDragAssignment(null);
                              }
                            : undefined,
                    )
                }
                applications={applicationOptions}
                users={userOptions}
            />
            <CreateApplicationModal
                open={applicationModalOpen}
                onCancel={() => {
                    setApplicationModalOpen(false);
                    setPendingDragAssignment(null);
                }}
                onApply={(values) =>
                    handleCreateApplication(
                        values,
                        pendingDragAssignment
                            ? (newId) => {
                                  handleDragAssignVMs(pendingDragAssignment.sourceIds, undefined, newId);
                                  setPendingDragAssignment(null);
                              }
                            : undefined,
                    )
                }
                moveGroups={moveGroupOptions}
            />
            <EditMoveGroupModal
                open={editMoveGroupModalOpen}
                onCancel={() => {
                    setEditMoveGroupModalOpen(false);
                    setEditingMoveGroup(null);
                }}
                onApply={handleUpdateMoveGroup}
                moveGroup={editingMoveGroup}
                applications={applicationOptions}
                users={userOptions}
            />
            <EditApplicationModal
                open={editApplicationModalOpen}
                onCancel={() => {
                    setEditApplicationModalOpen(false);
                    setEditingApplication(null);
                }}
                onApply={handleUpdateApplication}
                application={editingApplication}
                moveGroups={moveGroupOptions}
                users={[]} // TODO: Add users when endpoint is available
            />
            {/* Render AssignFromUnsortedModal when assigning from all/unassigned inventory */}
            {selectedInventory || selectedUnassigned ? (
                <AssignFromUnsortedModal
                    open={manageModalOpen}
                    onCancel={() => setManageModalOpen(false)}
                    onApply={handleManageApply}
                    moveGroups={moveGroupOptions}
                    applications={applicationOptions}
                    users={[]} // TODO: Add users when endpoint is available
                    selectedRowKeys={selectedRowKeys}
                />
            ) : (
                /* Render EditAssignedModal when editing VMs from move groups or applications */
                <EditAssignedModal
                    open={manageModalOpen}
                    onCancel={() => setManageModalOpen(false)}
                    onApply={handleManageApply}
                    moveGroups={moveGroupOptions}
                    applications={applicationOptions}
                    users={[]} // TODO: Add users when endpoint is available
                    selectedRowKeys={selectedRowKeys}
                />
            )}
            <DeleteModal
                open={deleteModalOpen}
                title="Confirm"
                message={deleteModalMessage}
                warningMessage="Once deleted, it cannot be recovered."
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={() => {
                    setDeleteModalOpen(false);
                    deleteModalHandler();
                }}
            />
            <DragAssignmentConfirmModal
                open={dragAssignModalOpen}
                title={dragAssignModalTitle}
                message={dragAssignModalMessage}
                onCancel={() => setDragAssignModalOpen(false)}
                onConfirm={() => {
                    setDragAssignModalOpen(false);
                    dragAssignHandler();
                }}
            />
        </Layout>
    );
};
